import React, { useState, useEffect, useMemo } from "react";
import { DataRequest, DataRequestStatus } from "../types/api";
import {
  STATUS_GROUPS,
  STATUS_BADGE,
  requestType,
  formatDate,
} from "./YourRequestsPage";
import { ImportWizard } from "./ImportWizard";
import { ForwardRequestPanel } from "./ForwardRequestPanel";
import { RequestComments } from "./RequestComments";
import { apiService } from "../services/api";

interface AdminRequestsSplitViewProps {
  requests: DataRequest[];
  datasetNames: Record<number, string>;
  // The viewer's own user ID — needed by the import wizard when fulfilling a
  // data request (locks them into their own default download access).
  currentUserId?: number;
  // The viewer's global role. Only used to hide the Forward action from org
  // managers: the redirect endpoint rejects them outright
  // (admin_handlers.go: "Manager role cannot redirect data requests"), so
  // showing the button would be an affordance the backend then 403s.
  currentUserRole?: string;
  onReview: (
    requestId: number,
    status: "approved" | "rejected",
    notes: string,
  ) => Promise<void>;
  // Called after a data request's file is uploaded, so the parent can refetch
  // and the request naturally transitions to look like an access request.
  onDataUploaded: () => void;
  // Global admin view (aggregates requests across every project the viewer
  // manages): adds a Project filter and shows the project name per row.
  showProjectFilter?: boolean;
  // Archived projects are read-only: the server refuses every review action, so
  // the decision controls are replaced by a note rather than offered and 409'd.
  archived?: boolean;
  // Externally controlled initial selection, for deep-linking to one request.
  selectedId?: number;
  onSelect?: (id: number) => void;
}

type SortBy = "created_newest" | "created_oldest" | "status";

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const InfoIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

export const AdminRequestsSplitView: React.FC<AdminRequestsSplitViewProps> = ({
  requests,
  datasetNames,
  currentUserId,
  currentUserRole,
  onReview,
  onDataUploaded,
  showProjectFilter = false,
  archived = false,
  selectedId: externalSelectedId,
  onSelect,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(
    externalSelectedId ?? requests[0]?.id ?? null,
  );
  const [filterProject, setFilterProject] = useState<number | "all">("all");
  const [filterStatus, setFilterStatus] = useState<string | "all">("all");
  const [filterType, setFilterType] = useState<"access" | "data" | "all">(
    "all",
  );
  const [sortBy, setSortBy] = useState<SortBy>("created_newest");
  const [reviewNotes, setReviewNotes] = useState("");
  const [defaultExpiryDate, setDefaultExpiryDate] = useState("");

  useEffect(() => {
    apiService
      .getUploadConfig()
      .then((cfg) => {
        const d = new Date();
        d.setDate(d.getDate() + cfg.retention_days);
        setDefaultExpiryDate(d.toISOString().slice(0, 10));
      })
      .catch(() => {});
  }, []);
  const [reviewing, setReviewing] = useState(false);
  // Request whose forward panel is open, so switching selection closes it.
  const [forwardingId, setForwardingId] = useState<number | null>(null);

  useEffect(() => {
    if (selectedId == null && requests.length > 0)
      setSelectedId(requests[0].id);
  }, [requests, selectedId]);

  useEffect(() => {
    if (externalSelectedId != null) setSelectedId(externalSelectedId);
  }, [externalSelectedId]);

  useEffect(() => {
    setReviewNotes("");
  }, [selectedId]);

  const selected = requests.find((r) => r.id === selectedId) || null;

  const handleSelect = (id: number) => {
    setSelectedId(id);
    onSelect?.(id);
  };

  const projects = useMemo(() => {
    const map = new Map<number, string>();
    requests.forEach((r) => {
      if (!map.has(r.project_id))
        map.set(r.project_id, r.project_title || `Project ${r.project_id}`);
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let filtered = [...requests];
    if (filterProject !== "all")
      filtered = filtered.filter((r) => r.project_id === filterProject);
    if (filterStatus !== "all")
      filtered = filtered.filter((r) => r.status === filterStatus);
    if (filterType !== "all")
      filtered = filtered.filter((r) => requestType(r) === filterType);
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "created_newest":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "created_oldest":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "status": {
          const order = [
            "pending",
            "awaiting_confirmation",
            "approved",
            "fulfilled",
            "rejected",
            "redirected",
          ];
          return order.indexOf(a.status) - order.indexOf(b.status);
        }
        default:
          return 0;
      }
    });
    return filtered;
  }, [requests, filterProject, filterStatus, filterType, sortBy]);

  const grouped = useMemo(
    () =>
      STATUS_GROUPS.map((g) => ({
        ...g,
        items: filteredRequests.filter((r) => r.status === g.status),
      })).filter((g) => g.items.length > 0),
    [filteredRequests],
  );

  const handleApprove = async () => {
    if (!selected) return;
    setReviewing(true);
    try {
      await onReview(selected.id, "approved", reviewNotes.trim());
    } finally {
      setReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !reviewNotes.trim()) return;
    setReviewing(true);
    try {
      await onReview(selected.id, "rejected", reviewNotes.trim());
    } finally {
      setReviewing(false);
    }
  };

  const handleForwarded = () => {
    setForwardingId(null);
    // The request moves to "redirected" and gains a source row, so refetch.
    onDataUploaded();
  };

  if (requests.length === 0) {
    return (
      <div className="no-data">No data requests for this project yet.</div>
    );
  }

  return (
    <div className="arv-split">
      <style>{`
        .arv-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }
        .arv-filters select {
          padding: 7px 10px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          background: var(--surface-2);
          color: var(--text-primary);
        }
        .arv-filters select.arv-sort {
          margin-left: auto;
        }
        .arv-split-body {
          display: grid;
          grid-template-columns: 360px 1fr;
          border: 0.5px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          min-height: 480px;
        }
        .arv-list-panel {
          background: var(--surface-1);
          border-right: 0.5px solid var(--border);
          padding: 0.9rem;
          overflow: auto;
        }
        .arv-group-label {
          font-size: 12px;
          font-weight: 500;
          margin: 0 0 6px;
        }
        .arv-group-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          margin-bottom: 16px;
          background: var(--surface-2);
        }
        .arv-row {
          padding: 9px 10px;
          cursor: pointer;
          border-left: 2px solid transparent;
        }
        .arv-row:not(:last-child) {
          border-bottom: 0.5px solid var(--border);
        }
        .arv-row:hover {
          background: var(--surface-1);
        }
        .arv-row.selected {
          background: var(--bg-accent-muted);
          border-left-color: var(--border-accent);
        }
        .arv-row-title {
          font-size: 13px;
          font-weight: 500;
          margin: 0;
        }
        .arv-row.selected .arv-row-title {
          color: var(--text-accent);
        }
        .arv-row-sub {
          font-size: 11px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }
        .arv-type-pill {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 10px;
          color: var(--text-muted);
          background: var(--surface-1);
          padding: 1px 6px;
          border-radius: 8px;
          margin-top: 4px;
        }
        .arv-detail-pane {
          padding: 1.5rem 2rem;
          overflow: auto;
        }
        .arv-detail-project {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 6px;
        }
        .arv-detail-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--text-secondary);
          background: var(--surface-1);
          padding: 3px 9px;
          border-radius: var(--radius);
          margin-bottom: 8px;
        }
        .arv-detail-titlerow {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
          gap: 16px;
        }
        .arv-detail-title-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 2px;
        }
        .arv-detail-title {
          margin: 0;
          font-size: 20px;
          color: var(--text-primary);
        }
        .arv-detail-status-badge {
          font-size: 12px;
          font-weight: 500;
          padding: 4px 10px;
          border-radius: var(--radius);
          flex-shrink: 0;
        }
        .arv-detail-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 12px 0 20px;
          max-width: 560px;
        }
        .arv-detail-meta {
          display: flex;
          gap: 32px;
          padding: 14px 0;
          border-top: 0.5px solid var(--border);
          border-bottom: 0.5px solid var(--border);
          margin-bottom: 20px;
        }
        .arv-detail-meta-label {
          font-size: 11px;
          color: var(--text-muted);
          margin: 0 0 3px;
        }
        .arv-detail-meta-value {
          font-size: 13px;
          font-weight: 500;
          margin: 0;
          color: var(--text-primary);
        }
        .arv-files-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 0 0 6px;
        }
        .arv-files-empty {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: var(--surface-1);
          border-radius: var(--radius);
          color: var(--text-muted);
          font-size: 13px;
        }
        .arv-section-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin: 18px 0 6px;
        }
        .arv-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .arv-chip {
          padding: 3px 10px;
          border-radius: var(--radius);
          background: var(--bg-accent-muted);
          color: var(--text-accent);
          font-size: 12px;
          font-weight: 500;
        }
        .arv-criteria {
          margin: 0;
          padding-left: 20px;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .arv-criteria li {
          margin-bottom: 3px;
        }
        .arv-note-body {
          margin: 0;
          font-size: 13px;
          line-height: 1.55;
          color: var(--text-secondary);
          white-space: pre-wrap;
        }
        .arv-sources-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .arv-sources-table th {
          text-align: left;
          padding: 7px 10px;
          font-size: 11px;
          font-weight: 500;
          color: var(--text-muted);
          background: var(--surface-1);
          border-bottom: 0.5px solid var(--border);
        }
        .arv-sources-table td {
          padding: 7px 10px;
          border-bottom: 0.5px solid var(--border);
          color: var(--text-secondary);
        }
        .arv-sources-table tr:last-child td {
          border-bottom: none;
        }
        .arv-source-status {
          display: inline-block;
          padding: 2px 9px;
          border-radius: var(--radius);
          font-size: 12px;
          font-weight: 500;
        }
        .arv-file-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 4px;
          border-bottom: 0.5px solid var(--border);
        }
        .arv-file-row:last-child {
          border-bottom: none;
        }
        .arv-file-name {
          font-size: 13px;
          font-family: var(--font-mono, ui-monospace, monospace);
        }
        .arv-archived-note {
          margin: 0;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .arv-actions {
          border-top: 0.5px solid var(--border);
          padding-top: 20px;
          margin-top: 24px;
        }
        .arv-notes {
          width: 100%;
          padding: 9px 10px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          resize: vertical;
          margin-bottom: 12px;
          background: var(--surface-2);
          color: var(--text-primary);
          box-sizing: border-box;
        }
        .arv-decision-row {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
        }
        .arv-decision-btn {
          flex: 1;
          padding: 8px;
          border-radius: var(--radius);
          font-size: 14px;
          font-family: inherit;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          cursor: pointer;
        }
        .arv-decision-btn:disabled {
          background: var(--fill-disabled);
          color: var(--text-disabled);
          border-color: var(--border-disabled);
          cursor: not-allowed;
        }
        .arv-approve-btn {
          background: var(--bg-success);
          color: var(--text-success);
          border: 0.5px solid var(--border-success);
        }
        .arv-reject-btn {
          background: var(--bg-danger);
          color: var(--text-danger);
          border: 0.5px solid var(--border-danger);
        }
        .arv-forward-btn {
          width: 100%;
          padding: 7px;
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          color: var(--text-secondary);
          background: none;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
        }
        .arv-forward-btn:hover {
          background: var(--surface-1);
        }
      `}</style>

      <div className="arv-filters">
        {showProjectFilter && (
          <select
            value={filterProject}
            onChange={(e) =>
              setFilterProject(
                e.target.value === "all" ? "all" : Number(e.target.value),
              )
            }
          >
            <option value="all">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All statuses</option>
          {STATUS_GROUPS.map((g) => (
            <option key={g.status} value={g.status}>
              {g.label}
            </option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) =>
            setFilterType(e.target.value as "access" | "data" | "all")
          }
        >
          <option value="all">All types</option>
          <option value="access">Access request</option>
          <option value="data">Data request</option>
        </select>
        <select
          className="arv-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortBy)}
        >
          <option value="created_newest">Newest first</option>
          <option value="created_oldest">Oldest first</option>
          <option value="status">Status priority</option>
        </select>
      </div>

      <div className="arv-split-body">
        <div className="arv-list-panel">
          {grouped.length === 0 ? (
            <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
              No requests match these filters.
            </p>
          ) : (
            grouped.map((group) => (
              <div key={group.status}>
                <p
                  className="arv-group-label"
                  style={{ color: group.labelColor }}
                >
                  {group.label} ({group.items.length})
                </p>
                <div className="arv-group-list">
                  {group.items.map((request) => {
                    const type = requestType(request);
                    return (
                      <div
                        key={request.id}
                        className={`arv-row ${selectedId === request.id ? "selected" : ""}`}
                        onClick={() => handleSelect(request.id)}
                      >
                        <p className="arv-row-title">{request.title}</p>
                        <p className="arv-row-sub">
                          {request.user_name || `User ${request.user_id}`}
                          {showProjectFilter &&
                            ` · ${request.project_title || `Project ${request.project_id}`}`}{" "}
                          · {formatDate(request.created_at)}
                        </p>
                        <span className="arv-type-pill">
                          {type === "data" ? "Data request" : "Access request"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="arv-detail-pane">
          {selected && (
            <RequestDetail
              request={selected}
              datasetName={
                selected.dataset_id
                  ? datasetNames[selected.dataset_id]
                  : undefined
              }
              reviewNotes={reviewNotes}
              onNotesChange={setReviewNotes}
              onApprove={handleApprove}
              onReject={handleReject}
              canForward={currentUserRole !== "manager"}
              forwarding={forwardingId === selected.id}
              onStartForward={() => setForwardingId(selected.id)}
              onCancelForward={() => setForwardingId(null)}
              onForwarded={handleForwarded}
              onDataUploaded={onDataUploaded}
              reviewing={reviewing}
              archived={archived}
              currentUserId={currentUserId}
              defaultExpiryDate={defaultExpiryDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface RequestDetailProps {
  request: DataRequest;
  datasetName?: string;
  reviewNotes: string;
  onNotesChange: (notes: string) => void;
  onApprove: () => void;
  onReject: () => void;
  canForward: boolean;
  forwarding: boolean;
  onStartForward: () => void;
  onCancelForward: () => void;
  onForwarded: () => void;
  onDataUploaded: () => void;
  reviewing: boolean;
  archived: boolean;
  currentUserId?: number;
  defaultExpiryDate: string;
}

const SOURCE_STATUS_TONE: Record<string, { bg: string; text: string }> = {
  pending: { bg: "var(--bg-warning)", text: "var(--text-warning)" },
  fulfilled: { bg: "var(--bg-success)", text: "var(--text-success)" },
  declined: { bg: "var(--bg-danger)", text: "var(--text-danger)" },
};

const RequestDetail: React.FC<RequestDetailProps> = ({
  request,
  datasetName,
  reviewNotes,
  onNotesChange,
  onApprove,
  onReject,
  canForward,
  forwarding,
  onStartForward,
  onCancelForward,
  onForwarded,
  onDataUploaded,
  reviewing,
  archived,
  currentUserId,
  defaultExpiryDate,
}) => {
  const type = requestType(request);
  const badge =
    STATUS_BADGE[request.status as DataRequestStatus] || STATUS_BADGE.pending;
  const titleLabel = type === "data" ? "Request title" : "Request";
  // A forwarded request is still awaiting a decision: gating the review actions
  // on "pending" alone left redirected requests permanently unactionable.
  const isActionable =
    !archived &&
    (request.status === "pending" || request.status === "redirected");
  const needsUpload = request.status === "approved" && type === "data";

  return (
    <>
      <p className="arv-detail-project">
        {request.project_title || `Project ${request.project_id}`} · requested
        by {request.user_name || `User ${request.user_id}`}
        {request.user_email ? ` (${request.user_email})` : ""}
      </p>
      <span className="arv-detail-type-badge">
        {type === "data" ? "Data request" : "Access request"}
      </span>

      <div className="arv-detail-titlerow">
        <div>
          <p className="arv-detail-title-label">{titleLabel}</p>
          <h2 className="arv-detail-title">{request.title}</h2>
        </div>
        <span
          className="arv-detail-status-badge"
          style={{ background: badge.bg, color: badge.text }}
        >
          {badge.label}
        </span>
      </div>

      <p className="arv-detail-desc">{request.description}</p>

      <div className="arv-detail-meta">
        <div>
          <p className="arv-detail-meta-label">Submitted</p>
          <p className="arv-detail-meta-value">
            {formatDateTime(request.created_at)}
          </p>
        </div>
      </div>

      {request.requested_fields && request.requested_fields.length > 0 && (
        <>
          <p className="arv-section-label">Requested fields</p>
          <div className="arv-chip-row">
            {request.requested_fields.map((field, i) => (
              <span key={i} className="arv-chip">
                {field}
              </span>
            ))}
          </div>
        </>
      )}

      {request.filter_criteria && request.filter_criteria.length > 0 && (
        <>
          <p className="arv-section-label">Filter criteria</p>
          <ul className="arv-criteria">
            {request.filter_criteria.map((criterion, i) => (
              <li key={i}>{criterion}</li>
            ))}
          </ul>
        </>
      )}

      {request.processing_notes && (
        <>
          <p className="arv-section-label">Processing notes from requester</p>
          <p className="arv-note-body">{request.processing_notes}</p>
        </>
      )}

      {request.admin_notes && (
        <>
          <p className="arv-section-label">Review notes</p>
          <p className="arv-note-body">{request.admin_notes}</p>
        </>
      )}

      {type === "data" ? (
        needsUpload ? (
          <>
            <p className="arv-files-label">Upload data</p>
            <ImportWizard
              key={`upload-${request.id}`}
              projectId={request.project_id}
              currentUserId={currentUserId || 0}
              defaultDownloadScope="none"
              uploadType="admin_data_request"
              inspectRoute={`/admin/data-requests/${request.id}/upload/inspect`}
              requestedFields={request.requested_fields}
              defaultExpiryDate={defaultExpiryDate}
              onUpload={async ({
                file,
                columns,
                displayName,
                metadataScope,
                downloadScope,
                downloadUserIds,
                parseOptions,
                piiFindingsAcknowledged,
                piiValueRedactions,
                piiDroppedColumns,
                datasetExpiry,
              }) => {
                await apiService.uploadDataFileWithColumns(
                  request.id,
                  file,
                  columns,
                  metadataScope,
                  [],
                  downloadScope,
                  downloadScope === "selected_members" ? downloadUserIds : [],
                  datasetExpiry,
                  displayName || undefined,
                  piiFindingsAcknowledged ?? false,
                  piiValueRedactions ?? [],
                  piiDroppedColumns ?? [],
                  parseOptions,
                );
                onDataUploaded();
              }}
              onCancel={() => {}}
            />
          </>
        ) : (
          <>
            <p className="arv-files-label">Files</p>
            <div className="arv-files-empty">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Not uploaded yet — you'll be asked to upload once this request is
              approved.
            </div>
          </>
        )
      ) : (
        <>
          <p className="arv-files-label">Requested files (1)</p>
          <div className="arv-file-row">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-muted)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6M9 13h6M9 17h6" />
            </svg>
            <span className="arv-file-name">
              {datasetName || request.title}
            </span>
          </div>
        </>
      )}

      {request.sources && request.sources.length > 0 && (
        <>
          <p className="arv-section-label">Forwarded to</p>
          <table className="arv-sources-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Forwarded</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {request.sources.map((src) => {
                const tone = SOURCE_STATUS_TONE[src.status] || SOURCE_STATUS_TONE.pending;
                const recipient = src.organization_name
                  ? `${src.organization_name} (org)`
                  : src.user_name
                    ? `${src.user_name}${src.user_email ? ` (${src.user_email})` : ""}`
                    : "Unknown";
                return (
                  <tr key={src.id}>
                    <td>{recipient}</td>
                    <td>{formatDateTime(src.created_at)}</td>
                    <td>
                      <span
                        className="arv-source-status"
                        style={{ background: tone.bg, color: tone.text }}
                      >
                        {src.status}
                      </span>
                    </td>
                    <td>{src.notes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <div style={{ marginTop: "20px" }}>
        <RequestComments
          requestId={request.id}
          requestStatus={request.status}
          initialExpanded={true}
        />
      </div>

      {archived && (
        <div className="arv-actions">
          <p className="arv-archived-note">
            This project is archived and read-only. Restore it in the project's
            Settings -`{'>'}` Project Controls to act on this request.
          </p>
        </div>
      )}

      {isActionable && canForward && forwarding && (
        <div className="arv-actions">
          <ForwardRequestPanel
            requestId={request.id}
            onCancel={onCancelForward}
            onForwarded={onForwarded}
          />
        </div>
      )}

      {isActionable && !(canForward && forwarding) && (
        <div className="arv-actions">
          <textarea
            className="arv-notes"
            rows={2}
            placeholder="Add review notes (required for rejection)…"
            value={reviewNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={reviewing}
          />
          <div className="arv-decision-row">
            <button
              className="arv-decision-btn arv-approve-btn"
              onClick={onApprove}
              disabled={reviewing}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Approve
            </button>
            <button
              className="arv-decision-btn arv-reject-btn"
              onClick={onReject}
              disabled={reviewing || !reviewNotes.trim()}
              title={
                !reviewNotes.trim()
                  ? "Review notes are required to reject a request"
                  : undefined
              }
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
              Reject
            </button>
          </div>
          {canForward && (
          <button className="arv-forward-btn" onClick={onStartForward}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m22 2-7 20-4-9-9-4Z" />
              <path d="M22 2 11 13" />
            </svg>
            Forward to data supplier
            <span title="Ask a data uploader or PI to provide the data for this request">
              <InfoIcon />
            </span>
          </button>
          )}
        </div>
      )}
    </>
  );
};
